<template>
  <div>
    <!-- Journal Entries Card -->
    <b-card class="leave-requests-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>

      <!-- Card Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2 mt-3 px-2">
        <div>
          <h5 class="mb-0">{{ pageTitle }} Entries</h5>
          <small class="text-muted">Manage and review {{ pageTitle.toLowerCase() }} entries</small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-3 mt-1 px-1">
        <!-- Search -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <div style="position: relative;">
            <b-form-input
              v-model.trim="searchQuery"
              :placeholder="$t('Search by description/amount...')"
              class="modern-input"
              style="padding-right: 4.5rem !important;"
              @keyup.enter="LoadSearch()"
            />
            <div style="position: absolute; right: 12px; top: 50%; transform: translateY(-50%); display: flex; align-items: center; gap: 8px;">
              <feather-icon 
                v-if="searchQuery"
                icon="XIcon" 
                size="16" 
                class="cursor-pointer text-muted hover-primary-icon" 
                @click="searchQuery = ''; LoadSearch()"
                v-b-tooltip.hover.top="$t('Clear Search')"
              />
              <div v-if="searchQuery" style="width: 1px; height: 16px; background-color: #e0d8ff;"></div>
              <feather-icon 
                icon="SearchIcon" 
                size="18" 
                class="cursor-pointer text-primary hover-primary-icon" 
                @click="LoadSearch()"
                v-b-tooltip.hover.top="$t('Filter Data')"
              />
            </div>
          </div>
        </b-col>

        <!-- Account Filter -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :clearable="true"
            :options="accountOptions"
            :reduce="(opt) => opt.id"
            label="title"
            v-model="filterAccountID"
            :placeholder="$t('All Accounts')"
            class="custom-filter-select"
            append-to-body
            @input="filterData(1)"
          >
            <template #option="{ title, code }">
              <span class="mr-50" v-if="code" style="color: #7c5dfa; font-weight: 600;">[{{ code }}]</span> {{ title }}
            </template>
          </v-select>
        </b-col>

        <!-- Status Filter -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :clearable="false"
            :options="statusOptions"
            :reduce="(opt) => opt.value"
            label="text"
            v-model="filterStatus"
            :placeholder="$t('All Status')"
            class="custom-filter-select"
            append-to-body
            @input="setStatus()"
          />
        </b-col>

        <!-- Voucher Type (bank/credit/all) -->
        <b-col v-if="showVoucherTypeDropdown" xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <v-select
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :clearable="false"
            :options="filteredVoucherTypes"
            :reduce="(opt) => opt.value"
            label="text"
            v-model="selectedVoucherType"
            :placeholder="$t('All Types')"
            class="custom-filter-select"
            append-to-body
            @input="filterData(1)"
          />
        </b-col>

        <!-- Payee Filter -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <b-form-input
            v-model.trim="filterPayee"
            :placeholder="$t('Filter by Payee')"
            class="modern-input"
            @keyup.enter="filterData(1)"
          />
        </b-col>

        <!-- Cheque No Filter -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <b-form-input
            v-model.trim="filterChequeNo"
            :placeholder="$t('Filter by Cheque#')"
            class="modern-input"
            @keyup.enter="filterData(1)"
          />
        </b-col>

        <!-- Date From / To -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <flat-pickr
            v-model="dtObj.dtFrom"
            :config="configSingle"
            class="form-control modern-input"
            :placeholder="$t('Date From')"
            @on-change="setDateFrom"
          />
        </b-col>
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
          <flat-pickr
            v-model="dtObj.dtTo"
            :config="configSingle"
            class="form-control modern-input"
            :placeholder="$t('Date To')"
            @on-change="setDateTo"
          />
        </b-col>

        <!-- Action Buttons -->
        <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1 ml-lg-auto text-lg-right">
          <div class="d-flex align-items-center justify-content-lg-end">
            <!-- Search Button -->
            <b-button
              @click="applyFilters()"
              v-ripple.400="'rgba(115, 103, 240, 0.15)'"
              variant="outline-primary"
              class="action-btn-search mr-2"
              style="height: 44px; width: 44px; min-width: 44px; display: flex; align-items: center; justify-content: center; background-color: #fff !important; border: 1px solid #e0d8ff !important; border-radius: 8px !important; padding: 0 !important;"
              v-b-tooltip.hover.top="$t('Filter Data')"
            >
              <feather-icon icon="SearchIcon" size="18" class="text-primary" />
            </b-button>

            <!-- Add Button -->
            <b-button
              v-if="rights.add"
              @click="Edit(0)"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="gradient-primary"
              class="action-btn-filter px-4 py-2"
              style="min-width: 170px; justify-content: center; height: 44px; border: 1px solid #fff !important;"
            >
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              <span class="font-weight-bold">{{ pageTitle }}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-1">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="filterData(1)"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ paginationRange.start }}</strong>
            {{ $t("to") }} <strong>{{ paginationRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalRows }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="filterData()"
          />
        </div>
      </div>


      <!-- Data Grid with Horizontal Scroll -->
      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="leave-requests-table"
            :items="items"
            :fields="fields"
            :busy="dataLoading"
            :responsive="false"
            show-empty
            hover
          >
            <template #table-busy>
              <div class="skeleton-table-wrapper">
                <div v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row-item">
                  <div
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell-item"
                  >
                    <div class="skeleton-loader"></div>
                  </div>
                </div>
              </div>
            </template>

            <template #head()="data">
              <span>{{ $t(data.label) }}</span>
            </template>

            <!-- S.No -->
            <template #cell(sno)="{ index }">
              <span class="lr-sno-text">{{
                (currentPage - 1) * perPage + index + 1
              }}</span>
            </template>

            <template #cell(voucherNum)="data">
              <div class="journal-voucher-cell">
                <b-badge variant="light-primary" class="mb-50 journal-type-badge">
                  {{ data.item.entry.voucherType }}
                </b-badge>
                <br />
                <b-badge variant="light-primary" class="journal-num-badge">
                  {{ data.item.entry.voucherNum }}
                </b-badge>
              </div>
            </template>

            <template #cell(date)="data">
              <div class="lr-date-cell">
                <feather-icon icon="CalendarIcon" size="12" class="lr-date-icon" />
                <span>{{ new Date(data.item.entry.date).toLocaleDateString('en-UK', { year: 'numeric', day: 'numeric', month: 'short' }) }}</span>
              </div>
              <b-badge
                :variant="getVariant(data.item.entry.status)"
                class="mt-50"
                v-if="data.item.entry.status && data.item.entry.status != 'active'"
              >
                {{ data.item.entry.status }}
              </b-badge>
            </template>

            <template #cell(account)="data">
              <div v-for="rec in data.item.details" :key="rec.id || rec" class="journal-account-row mb-25">
                <span class="journal-account-title">{{ rec.meta_accountTitle }}</span>
              </div>
            </template>

            <template #cell(debit)="data">
              <div v-for="rec in data.item.details" :key="rec.id || rec" class="journal-amount-row mb-25">
                <span :class="rec.debit ? 'journal-amount-debit' : 'journal-amount-zero'">{{ rec.debit }}</span>
              </div>
              <div class="journal-total-row border-top-secondary pt-25">
                <span class="journal-total-label">Total:</span>
                <strong class="journal-total-value">{{ debitTotal(data.item.details) }}</strong>
              </div>
            </template>

            <template #cell(credit)="data">
              <div v-for="rec in data.item.details" :key="rec.id || rec" class="journal-amount-row mb-25">
                <span :class="rec.credit ? 'journal-amount-credit' : 'journal-amount-zero'">{{ rec.credit }}</span>
              </div>
              <div class="journal-total-row border-top-secondary pt-25">
                <span class="journal-total-label">Total:</span>
                <strong class="journal-total-value">{{ creditTotal(data.item.details) }}</strong>
              </div>
            </template>

            <template #cell(chequeNo)="data">
              <div
                v-for="rec in data.item.details"
                :key="rec.id || rec"
                class="journal-cheque-wrap mb-25"
              >
                <span v-if="rec.chequeNo" class="journal-cheque-badge">
                  <feather-icon
                    icon="CreditCardIcon"
                    size="11"
                    class="mr-25 journal-cheque-icon"
                  />
                  <span class="journal-cheque-text">{{ rec.chequeNo }}</span>
                </span>
                <span v-else class="text-muted-empty">-</span>
              </div>
            </template>

            <template #cell(note)="data">
              <div v-for="rec in data.item.details" :key="rec.id || rec" class="mb-25">
                <span v-if="rec.description" class="journal-note-text">{{ rec.description }}</span>
                <span v-else class="text-muted-empty">-</span>
              </div>
            </template>

            <template #head(actions)="data">
              <div class="text-right mr-1">
                <span>{{ data.label }}</span>
              </div>
            </template>

            <template #cell(actions)="data">
              <div class="d-flex align-items-center justify-content-end">
                <!-- Edit / View -->
                <b-button
                  v-if="rights.edit"
                  variant="gradient-primary"
                  v-b-tooltip.hover.left
                  :title="$t('Edit')"
                  size="sm"
                  class="action-btn-table mr-1"
                  @click.stop="Edit(data.item)"
                >
                  <feather-icon icon="EditIcon" size="16" />
                </b-button>

                <!-- Review: move to pending -->
                <b-button
                  v-if="rights.review && data.item.entry.status && data.item.entry.status == 'under review'"
                  variant="outline-success"
                  v-b-tooltip.hover.top
                  :title="$t('Move to Pending')"
                  size="sm"
                  class="action-btn-outline approve-btn mr-1"
                  @click.stop="updateEntry(data.item.entry)"
                >
                  <feather-icon icon="CheckIcon" size="16" />
                </b-button>

                <!-- Print Cheque (approved only) -->
                <template v-if="data.item.entry.status == 'approved'">
                  <b-dropdown
                    v-if="bankVisible.includes(reportDomain)"
                    no-caret
                    :right="$store.state.appConfig.isRTL"
                    class="mr-1"
                    toggle-class="action-btn-table btn-sm"
                    variant="primary"
                    v-b-tooltip.hover.top
                    :title="$t('Print Cheque')"
                  >
                    <template #button-content>
                      <feather-icon icon="PrinterIcon" size="16" />
                    </template>
                    <b-dropdown-item
                      v-for="(dd, ind) in bankOptions"
                      :key="ind"
                      @click.stop="print(data.item.entry.id, dd.value)"
                    >
                      <span class="align-middle text-capitalize">{{ dd.name }}</span>
                    </b-dropdown-item>
                  </b-dropdown>

                  <b-button
                    v-else
                    variant="outline-primary"
                    v-b-tooltip.hover.top
                    :title="$t('Print Cheque')"
                    size="sm"
                    class="action-btn-outline mr-1"
                    @click.stop="print(data.item.entry.id, '')"
                  >
                    <feather-icon icon="PrinterIcon" size="16" />
                  </b-button>
                </template>

                <!-- Print Voucher -->
                <b-button
                  variant="outline-primary"
                  v-b-tooltip.hover.top
                  :title="$t('Print Voucher')"
                  size="sm"
                  class="action-btn-outline mr-1"
                  @click.stop="printVoucher(data.item.entry.id)"
                >
                  <feather-icon icon="FileTextIcon" size="16" />
                </b-button>

                <!-- Delete -->
                <b-button
                  v-if="rights.delete"
                  variant="outline-danger"
                  v-b-tooltip.hover.right
                  :title="$t('Delete')"
                  size="sm"
                  class="action-btn-outline reject-btn"
                  @click.stop="Delete(data.item.entry.id)"
                >
                  <feather-icon icon="Trash2Icon" size="16" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>
  </div>
</template>
  
  <script>
  import {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BImg,
    VBTooltip,
    BFormGroup,
    BForm,
    BMediaAside,
    BAside,
    BSpinner,
    BCardTitle,
    BCardText,
    BSidebar,
    VBToggle,
    BInputGroupPrepend,
    BInputGroup,
  } from "bootstrap-vue";
  import Ripple from "vue-ripple-directive";
  import vSelect from "vue-select";
  import flatPickr from "vue-flatpickr-component";
  
  import { mapActions, mapGetters } from "vuex";
  import roleCoaAccessMixin from "@/mixins/roleCoaAccessMixin";
  import { syncAccountCoaRestrictionsToStore } from "@/utils/roleCoaAccess";

  export default {
    mixins: [roleCoaAccessMixin],
    components: {
      BCard,
      BRow,
      BBadge,
      BTable,
      BCol,
      BFormInput,
      BButton,
      BSidebar,
      BFormGroup,
      BInputGroupPrepend,
      BInputGroup,
      BForm,
      BPagination,
      BSpinner,
      BDropdown,
      BDropdownItem,
      flatPickr,
      vSelect,
    },
    props: {
    voucherMode: {
      type: String,
      default: "journal", // journal, bank, credit, transfer, all
    },
  },
    directives: {
      "b-tooltip": VBTooltip,
      "b-toggle": VBToggle,
      Ripple,
    },
    async created() {
      syncAccountCoaRestrictionsToStore(
        this.$store,
        this.$store.state.loggedUser?.forms
      );
      const priorRights = { ...(this.$store.state.rights || {}) };
      let right = {};
      this.$store.state.menu.forEach((el) => {
        el.children.forEach((ch) => {
          right[ch.route] = {
            view: ch.view,
            add: ch.add,
            edit: ch.edit,
            delete: ch.delete,
            hideFee: ch.hideFee,
            review: ch.review,
            disableDate: ch.disableDate,
            coaAccountIDs:
              ch.coaAccountIDs || priorRights[ch.route]?.coaAccountIDs || [0],
          };
        });
      });
      // console.log(right);
      this.$store.commit("setRights", { ...priorRights, ...right });
  
      // Map route names to base rights
      const routeRightsMap = {
        "journal-advance": "journal-advance",
        "bank-voucher": "journal-advance",
        "credit-voucher": "journal-advance",
        "transfer-voucher": "journal-advance",
        "all-voucher": "all-voucher",
      };
      const baseRoute = routeRightsMap[this.$route.name] || this.$route.name;

      if (this.$store.state.rights[baseRoute]) {
        this.rights = this.$store.state.rights[baseRoute];
      } else {
        // Default rights if menu right not found
        this.rights = {
          view: true,
          add: true,
          edit: true,
          delete: true,
          review: false,
        };
      }
      
      // Default to "All" so users can see all voucher types and filter if needed
      // For bank mode: shows all BPV and BRV by default
      // For credit mode: shows all CPV and CRV by default
      // For all mode: shows every voucher type
      this.selectedVoucherType = "";
      
      this.LoadAccounts();
      this.LoadSettingsAll();
    },
    computed: {
      paginationRange() {
        if (this.totalRows === 0) return { start: 0, end: 0 };
        const start = (this.currentPage - 1) * this.perPage + 1;
        const end = Math.min(this.currentPage * this.perPage, this.totalRows);
        return { start, end };
      },
      // Page title based on mode
      pageTitle() {
        switch (this.voucherMode) {
          case "bank":
            return "Bank Voucher";
          case "credit":
            return "Cash Voucher";
          case "transfer":
            return "Transfer Voucher";
          case "all":
            return "All Voucher";
          default:
            return "Journal";
        }
      },
      // Show voucher type dropdown for bank, credit, and all modes
      showVoucherTypeDropdown() {
        return (
          this.voucherMode === "bank" ||
          this.voucherMode === "credit" ||
          this.voucherMode === "all"
        );
      },
      // Filtered voucher type options based on mode
      filteredVoucherTypes() {
        if (this.voucherMode === "all") {
          return this.allVoucherTypes;
        }
        if (this.voucherMode === "bank") {
          return [
            { text: "All Types", value: "" },
            { text: "BPV", value: "BPV" },
            { text: "BRV", value: "BRV" },
          ];
        } else if (this.voucherMode === "credit") {
          return [
            { text: "All Types", value: "" },
            { text: "CPV", value: "CPV" },
            { text: "CRV", value: "CRV" },
          ];
        }
        return this.allVoucherTypes;
      },
      // Default voucher type for the mode (used for JV and transfer)
      defaultVoucherType() {
        if (this.voucherMode === "all") {
          return this.selectedVoucherType || "JV";
        }
        if (this.voucherMode === "journal" || this.voucherMode === "transfer") {
          return "JV";
        }
        return "";
      },
      // Voucher types to filter data by (empty for all = no type restriction)
      voucherTypesForFilter() {
        if (this.voucherMode === "all") {
          return [];
        }
        if (this.voucherMode === "bank") {
          return ["BPV", "BRV"];
        } else if (this.voucherMode === "credit") {
          return ["CPV", "CRV"];
        } else if (this.voucherMode === "journal" || this.voucherMode === "transfer") {
          return ["JV"];
        }
        return [];
      },
    },
    watch: {
      // Watch for voucherMode prop changes
      voucherMode: {
        immediate: false,
        handler(newMode, oldMode) {
          if (newMode !== oldMode && oldMode !== undefined) {
            // Clear existing data immediately
            this.items = [];
            this.totalRows = 0;
            // Reset filters
            this.selectedVoucherType = "";
            this.searchQuery = "";
            this.currentPage = 1;
            this.filterStatus = "";
            this.searchDate = "";
            this.dtObj = {
              dtFrom: null,
              dtTo: null,
            };
            // Reload data
            this.$nextTick(() => {
              this.filterData(1);
            });
          }
        },
      },
      // Watch for route changes
      '$route': {
        immediate: false,
        handler(to, from) {
          // Only reload if switching between voucher routes
          const voucherRoutes = [
            "journal-advance",
            "bank-voucher",
            "credit-voucher",
            "transfer-voucher",
            "all-voucher",
          ];
          if (voucherRoutes.includes(to.name) && (from === null || voucherRoutes.includes(from.name)) && to.name !== (from?.name || '')) {
            // Clear existing data immediately
            this.items = [];
            this.totalRows = 0;
            // Reset filters
            this.selectedVoucherType = "";
            this.searchQuery = "";
            this.currentPage = 1;
            this.filterStatus = "";
            this.searchDate = "";
            this.filterAccountID = "";
            this.filterPayee = "";
            this.filterChequeNo = "";
            this.dtObj = {
              dtFrom: null,
              dtTo: null,
            };
            // Reload data
            this.$nextTick(() => {
              this.filterData(1);
            });
          }
        },
      },
    },
    data() {
      return {
        config: {
          altFormat: "j M, Y",
          altInput: true,
          dateFormat: "Y-m-d",
          mode: "range",
        },
        configSingle: {
          altFormat: "j M, Y",
          altInput: true,
          dateFormat: "Y-m-d",
        },
        searchDate: "",
        dtObj: {
          dtFrom: null,
          dtTo: null,
        },
        rights: {},
        dataLoading: false,
        searchQuery: "",
        filterPayee: "",
        filterChequeNo: "",
        filterAccountID: "",
        accountOptions: [],
        currentPage: 1,
        perPage: 50,
        totalRows: 0,
        pageSizeOptions: [
          { label: "20", value: 20 },
          { label: "50", value: 50 },
          { label: "100", value: 100 },
          { label: "200", value: 200 },
        ],
        fields: [
          { label: "S.No", key: "sno", tdClass: "sno-cell" },
          { label: "Voucher#", key: "voucherNum", tdClass: "setCell" },
          { label: "Date", key: "date", tdClass: "setCell" },
          {
            label: "Account",
            key: "account",
            tdClass: "setCell journal-cell-wrap journal-cell-account",
            thClass: "journal-cell-account",
          },
          { label: "Debit", key: "debit" },
          { label: "Credit", key: "credit" },
          {
            label: "Cheque#",
            key: "chequeNo",
            tdClass: "setCell journal-cell-wrap journal-cell-cheque",
            thClass: "journal-cell-cheque",
          },
          {
            label: "Note",
            key: "note",
            tdClass: "setCell journal-cell-wrap journal-cell-note",
            thClass: "journal-cell-note",
          },
          { label: "Actions", key: "actions", tdClass: "actions-cell" },
        ],
        items: [],
        /** Full COA-filtered list when role restricts accounts (client pagination). */
        filteredJournalCache: [],
        filteredJournalCacheKey: "",
        statusOptions: [
          { text: "All Status", value: "" },
          { text: "Pending", value: "pending" },
          { text: "Approved", value: "approved" },
          { text: "Declined", value: "declined" },
        ],
        filterStatus: this.$store.state.journalStatus,
        // Voucher type options
        allVoucherTypes: [
          { text: "All Types", value: "" },
          { text: "Journal Voucher", value: "JV" },
          { text: "Bank Payment Voucher", value: "BPV" },
          { text: "Bank Receipt Voucher", value: "BRV" },
          { text: "Cash Payment Voucher", value: "CPV" },
          { text: "Cash Receipt Voucher", value: "CRV" },
        ],
        selectedVoucherType: "",
        reportDomain: this.$store.state.report.replace(/_/g, ""),
        bankVisible: ["myskoolelixir"],
        bankOptions: [
          { name: "Dubai Islamic Bank", value: "" },
          { name: "Bank Al Habib", value: "al-habib" },
          { name: "Faysal Bank", value: "faysal" },
        ],
        reviewObj: null,
      };
    },
    methods: {
      ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),
  
      syncScroll(e) {
        if (this.$refs.topScrollbar) {
          this.$refs.topScrollbar.scrollLeft = e.target.scrollLeft;
        }
      },

      resolveVoucherModeForEdit(voucherType) {
        if (this.voucherMode !== "all") return this.voucherMode;
        const type = voucherType || this.selectedVoucherType || "";
        if (type === "BPV" || type === "BRV") return "bank";
        if (type === "CPV" || type === "CRV") return "credit";
        if (this.voucherMode === "transfer") return "transfer";
        return "journal";
      },

      getVariant(status) {
        const vt =
          status == "approved"
            ? "success"
            : status == "under review"
            ? "warning"
            : "danger";
        return `light-${vt}`;
      },
      print(id, bank) {
        var url =
          `https://${this.reportDomain}.myskool.app/Account/PrintCheque?entryID=` +
          id +
          "&dbb=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&title=cash" +
          "&bank=" +
          bank;
  
        window.open(url, "_blank");
      },
  
      printVoucher(id) {
        var url =
          `https://${this.reportDomain}.myskool.app/Account/PrintEntryVoucher?entryID=` +
          id +
          "&dbb=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId;
  
        window.open(url, "_blank");
      },
  
      creditTotal(items) {
        let total = items.reduce(
          (sum, el) => (el.credit ? sum + el.credit : sum + 0),
          0
        );
        return total;
      },
      debitTotal(items) {
        let total = items.reduce(
          (sum, el) => (el.debit ? sum + el.debit : sum + 0),
          0
        );
        return total;
      },
  
      async LoadSettingsAll() {
        this.dataLoading = true;
  
        let res = await this.post({
          url:
            this.$store.state.domain +
            "Settings/getSettingsAll?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          body: ["journal_entry_review"],
          context: this,
          token: this.$store.state.userData.token,
          subjects: true,
        });
        // console.log(res);
        this.reviewObj = res.find((el) => el.key == "journal_entry_review");
        if (this.reviewObj?.valueBool) {
          this.statusOptions.splice(1, 0, {
            text: "Under Review",
            value: "under review",
          });
          // load under review if user has review right
          if (this.rights.review) {
            this.filterStatus = "under review";
            this.setStatus();
          }
        } else if (this.filterStatus == "under review") {
          // reset to all if no review setting and last selected was under review
          this.filterStatus = "";
          this.setStatus();
        }
        await this.loadRoleCoaAccessSetting();
        this.filterData();
      },
  
      async Edit(item) {
        const route =
          this.reportDomain == "myskoolhpgs"
            ? "accounting-project"
            : "accounting-advance";
  
        if (item == 0) {
          const mode = this.resolveVoucherModeForEdit(
            this.selectedVoucherType || this.defaultVoucherType
          );
          this.$router.push({ 
            name: route, 
            params: { id: 0 },
            query: { 
              voucherMode: mode,
              voucherType: this.selectedVoucherType || this.defaultVoucherType 
            }
          });
        } else {
          // console.log(item);
          const mode = this.resolveVoucherModeForEdit(
            item.entry && item.entry.voucherType
          );
          this.$router.push({
            name: route,
            params: { id: item.entry.id, data: item },
            query: { 
              voucherMode: mode 
            }
          });
        }
      },
  
      async LoadAccounts() {
        var obj = {
          url:
            this.$store.state.domain +
            "Accounts/LoadData?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const raw = await this.get(obj);
        const list = this.normalizeAccountsResponse(raw);
        this.accountOptions = this.filterAccountsByRole(list);
      },
  
      journalVoucherTypeQuery() {
        if (this.selectedVoucherType) return this.selectedVoucherType;
        if (this.voucherTypesForFilter.length > 0) {
          return this.voucherTypesForFilter.join(",");
        }
        return "";
      },

      journalListQueryKey() {
        return JSON.stringify({
          status: this.filterStatus,
          dtFrom: this.dtObj.dtFrom,
          dtTo: this.dtObj.dtTo,
          voucherType: this.journalVoucherTypeQuery(),
          payee: this.filterPayee,
          cheque: this.filterChequeNo,
          accountID: this.filterAccountID,
          coa: this.coaAccountIdsQueryParam(),
        });
      },

      buildJournalListRequest(page) {
        const voucherTypeParam = this.journalVoucherTypeQuery();
        const suffix =
          (voucherTypeParam ? "&voucherType=" + voucherTypeParam : "") +
          (this.filterPayee
            ? "&payee=" + encodeURIComponent(this.filterPayee)
            : "") +
          (this.filterChequeNo
            ? "&chequeNo=" + encodeURIComponent(this.filterChequeNo)
            : "") +
          (this.filterAccountID
            ? "&accountID=" + encodeURIComponent(this.filterAccountID)
            : "") +
          this.coaAccountIdsQueryParam();

        const base =
          this.$store.state.domain +
          "JournalEntry/" +
          (this.filterStatus !== ""
            ? "LoadDataByStatusPagination?cID=" +
              this.$store.state.userData.cId +
              "&db=" +
              this.$store.state.userData.db +
              "&status=" +
              this.filterStatus +
              "&page=" +
              page +
              suffix
            : "LoadDataPagination?cID=" +
              this.$store.state.userData.cId +
              "&db=" +
              this.$store.state.userData.db +
              "&dtFrom=" +
              (this.dtObj.dtFrom || "") +
              "&dtTo=" +
              (this.dtObj.dtTo || "") +
              "&page=" +
              page +
              suffix);

        return {
          url: base,
          token: this.$store.state.userData.token,
          dashboard: true,
        };
      },

      paginateFilteredJournalCache() {
        const list = this.filteredJournalCache || [];
        const start = (this.currentPage - 1) * this.perPage;
        this.items = list.slice(start, start + this.perPage);
        this.totalRows = list.length;
      },

      async fetchAllJournalRowsForQuery() {
        const API_PAGE_SIZE = 50;
        let page = 1;
        let allRaw = [];
        while (true) {
          const res = await this.get(this.buildJournalListRequest(page));
          if (Array.isArray(res)) break;
          const chunk = res.data || [];
          allRaw = allRaw.concat(chunk);
          if (chunk.length < API_PAGE_SIZE) break;
          page += 1;
          if (page > 500) break;
        }
        return allRaw;
      },

      async refreshJournalListCoaMode() {
        this.dataLoading = true;
        try {
          const allRaw = await this.fetchAllJournalRowsForQuery();
          this.filteredJournalCache = this.applyJournalListFilters(allRaw);
          this.filteredJournalCacheKey = this.journalListQueryKey();
          this.paginateFilteredJournalCache();
        } finally {
          this.dataLoading = false;
        }
      },

      async refreshJournalListServerPage() {
        this.dataLoading = true;
        try {
          const res = await this.get(
            this.buildJournalListRequest(this.currentPage)
          );
          if (!Array.isArray(res)) {
            let data = res.data || [];
            data = this.applyJournalListFilters(data);
            this.items = data;
            this.totalRows = res.total || data.length;
          } else {
            this.items = [];
            this.totalRows = 0;
          }
        } finally {
          this.dataLoading = false;
        }
      },

      async LoadData() {
        if (this.hasCoaJournalRestriction()) {
          await this.refreshJournalListCoaMode();
        } else {
          await this.refreshJournalListServerPage();
        }
      },
  
      setStatus() {
        this.$store.commit("setJournalStatus", this.filterStatus);
      },
      setDate(sd, dateStr) {
        // console.log(sd, dateStr);
        if (dateStr) {
          if (sd.length > 1) {
            let date = dateStr.split(" to ");
            if (date.length == 1) {
              this.dtObj.dtFrom = date[0];
              this.dtObj.dtTo = date[0];
            } else {
              this.dtObj.dtFrom = date[0];
              this.dtObj.dtTo = date[1];
            }
          } else {
            this.dtObj.dtFrom = dateStr;
            this.dtObj.dtTo = null;
          }
        } else {
          this.dtObj.dtFrom = null;
          this.dtObj.dtTo = null;
        }
        // console.log(this.dtObj);
      },

      setDateFrom(selectedDates, dateStr) {
        this.dtObj.dtFrom = dateStr || null;
      },

      setDateTo(selectedDates, dateStr) {
        this.dtObj.dtTo = dateStr || null;
      },
  
      async filterData(val) {
        this.$nextTick(async () => {
          if (val === 1) this.currentPage = 1;

          if (this.hasCoaJournalRestriction()) {
            const key = this.journalListQueryKey();
            if (
              !val &&
              key === this.filteredJournalCacheKey &&
              this.filteredJournalCache.length >= 0
            ) {
              this.paginateFilteredJournalCache();
              return;
            }
            await this.refreshJournalListCoaMode();
            return;
          }

          if (this.filterStatus === "" && !this.dtObj.dtFrom && !this.dtObj.dtTo) {
            await this.LoadData();
          } else {
            await this.refreshJournalListServerPage();
          }
        });
      },
      
      filterByVoucherType(data) {
        if (this.selectedVoucherType) {
          return data.filter(item => 
            item.entry?.voucherType === this.selectedVoucherType
          );
        } else if (this.voucherTypesForFilter.length > 0) {
          return data.filter(item => 
            this.voucherTypesForFilter.includes(item.entry?.voucherType)
          );
        }
        return data;
      },

      applyJournalListFilters(data) {
        if (!Array.isArray(data)) return [];
        let list = this.filterByVoucherType(data);
        list = this.filterJournalEntriesByRole(list);
        return list;
      },

      async LoadSearch() {
        if (this.searchQuery.trim() == "") {
          this.filterData(1);
        } else {
          this.dataLoading = true;
          var obj = {
            url:
              this.$store.state.domain +
              "JournalEntry/Search?db=" +
              this.$store.state.userData.db +
              "&cID=" +
              this.$store.state.userData.cId +
              "&keyword=" +
              encodeURIComponent(this.searchQuery.trim()),
            token: this.$store.state.userData.token,
          };
          let res = await this.get(obj);
          // Filter by voucher type
          const list = Array.isArray(res) ? res : res?.data || [];
          this.items = this.applyJournalListFilters(list);
          this.totalRows = this.items.length;
          this.filteredJournalCache = [];
          this.filteredJournalCacheKey = "";
          this.currentPage = 1;
          this.dataLoading = false;
        }
      },

      applyFilters() {
        if (this.searchQuery && this.searchQuery.trim() !== "") {
          this.LoadSearch();
          return;
        }
        this.filterData(1);
      },
  
      async updateEntry(item) {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: this.$t("You won't be able to revert this!"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: this.$t("Yes"),
        });
        if (result.isConfirmed) {
          let obj = { ...item };
          obj.status = "pending";
          var status = await this.put({
            url:
              this.$store.state.domain +
              "JournalEntry/" +
              obj.id +
              "?db=" +
              this.$store.state.userData.db +
              "&userID=" +
              this.$store.state.userData.userID,
            message: "Journal updated successfully!",
            context: this,
            body: obj,
            token: this.$store.state.userData.token,
          });
          if (status) this.filterData();
        }
      },
  
      async Delete(id) {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: this.$t("You won't be able to revert this!"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: this.$t("Yes, Delete it!"),
        });
        if (result.isConfirmed) {
          // console.log(id);
          var status = await this.delete({
            url:
              this.$store.state.domain +
              "JournalEntry/DeleteVoucher?cID=" +
              this.$store.state.userData.cId +
              "&db=" +
              this.$store.state.userData.db +
              "&id=" +
              id +
              "&userID=" +
              this.$store.state.userData.userID,
            message: "Journal deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          });
          if (status) this.filterData();
        }
      },
    },
  };
  </script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style lang="scss" scoped>
/* ---- Premium Container & Header ---- */
.leave-requests-list-card {
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.08) !important;
  border: none;
}
.gradient-line-wrapper {
  height: 4px;
  background: linear-gradient(90deg, #7367f0, #ce9ffc);
  margin: -1.5rem -1.5rem 1rem;
}
.table-header h5 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2d3748;
}

/* ---- Premium Filter Inputs ---- */
.modern-input {
  border-radius: 10px !important;
  border: 1.5px solid #e0d8ff !important;
  padding: 0.625rem 0.875rem !important;
  height: 44px;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #1e293b;
}
.modern-input:focus {
  border-color: #7c5dfa !important;
  box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
  outline: none;
}
.hover-primary-icon {
  transition: color 0.2s;
}
.hover-primary-icon:hover {
  color: #7c5dfa !important;
}

/* Custom Filter Select - Fix Double Layer */
.custom-filter-select {
  width: 100% !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
}
.custom-filter-select ::v-deep .vs__dropdown-toggle {
  background: #fff !important;
  border: 1.5px solid #e0d8ff !important;
  border-radius: 10px !important;
  height: 44px !important;
  min-height: 44px !important;
  padding: 0 12px !important;
  box-shadow: none !important;
  margin: 0 !important;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}
.custom-filter-select ::v-deep .vs__dropdown-toggle:hover {
  border-color: #b8a9ff !important;
}
.custom-filter-select ::v-deep .vs__dropdown-toggle:focus-within {
  border-color: #7c5dfa !important;
  box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
}
.custom-filter-select ::v-deep .vs__dropdown-menu {
  border: 1.5px solid #e0d8ff !important;
  border-radius: 8px !important;
  box-shadow: 0 10px 25px rgba(124, 93, 250, 0.1) !important;
  padding: 8px !important;
  margin-top: 4px !important;
}
.custom-filter-select ::v-deep .vs__dropdown-option {
  padding: 10px 12px !important;
  border-radius: 6px !important;
  margin-bottom: 2px !important;
  color: #475569 !important;
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  display: flex !important;
  align-items: center !important;
}
.custom-filter-select ::v-deep .vs__dropdown-option:hover,
.custom-filter-select ::v-deep .vs__dropdown-option--highlight {
  background: #f8faff !important;
  color: #7c5dfa !important;
}
.custom-filter-select ::v-deep .vs__selected-options {
  display: flex !important;
  align-items: center !important;
  flex-grow: 1 !important;
  padding: 0 !important;
  margin: 0 !important;
  min-width: 0 !important;
  overflow: hidden !important;
}
.custom-filter-select ::v-deep .vs__selected {
  position: relative !important;
  z-index: 5 !important;
  margin: 0 !important;
  padding: 0 !important;
  color: #2d1b69 !important;
  font-weight: 500 !important;
  font-size: 0.9rem !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 100% !important;
}
.custom-filter-select ::v-deep .vs__search {
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  color: #2d1b69 !important;
  font-size: 0.9rem !important;
  height: 100% !important;
}
.custom-filter-select ::v-deep .vs__search::placeholder {
  color: #9ca3af !important;
  opacity: 1 !important;
}
.custom-filter-select ::v-deep .vs__actions {
  padding: 0 4px 0 8px !important;
  display: flex !important;
  align-items: center !important;
}
.action-btn-filter {
  border-radius: 8px;
  font-size: 0.85rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 10px rgba(115, 103, 240, 0.2) !important;
  transition: transform 0.2s;
}
.action-btn-filter:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(115, 103, 240, 0.3) !important;
}
.filter-show-btn {
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}
.filter-show-btn:hover {
  background: #7367f0;
  color: white;
}

/* ---- Table Action Buttons (matches studentLeaveRequest theme) ---- */
.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
.action-btn-table:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.action-btn-table:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
}
.approve-btn {
  border: 2px solid #10b981 !important;
  color: #10b981 !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
}
.approve-btn:hover:not(:disabled) {
  background: #f0fdf4 !important;
  border-color: #059669 !important;
  color: #059669 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}
.reject-btn {
  border: 2px solid #ef4444 !important;
  color: #ef4444 !important;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);
}
.reject-btn:hover:not(:disabled) {
  background: #fef2f2 !important;
  border-color: #dc2626 !important;
  color: #dc2626 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}

/* S.No column */
.lr-sno-text {
  color: #b9b9c3;
  font-size: 0.8rem;
  font-weight: 500;
}
.sno-cell {
  width: 50px;
  min-width: 50px;
}
.actions-cell {
  min-width: 170px;
  text-align: right;
}

/* ---- Pagination Controls (Reference: studentLeaveRequest) ---- */
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  @media (max-width: 992px) {
    flex-wrap: wrap;
    .pagination-count-display {
      order: 3;
      width: 100%;
      text-align: center;
      margin: 0.5rem 0 0 0;
    }
  }
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
  }
}

.page-size-select-compact {
  min-width: 75px;
  width: 75px;
  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0 8px !important;
    background: #fff !important;
    &:hover { border-color: #b8a9ff !important; }
    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }
  ::v-deep .vs__selected {
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
  }
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    strong {
      color: #7367f0; /* Matches the purple/blue theme in image */
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  ::v-deep .pagination {
    margin-bottom: 0;
    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      border-radius: 6px;
      margin: 0 2px;
      border: 1px solid #e0d8ff;
      color: #64748b;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      &:hover {
        background: #f3f0ff;
        color: #7c5dfa;
      }
    }
    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }
  }
}


/* ---- Scroll Container ---- */
.table-scroll-container {
  position: relative;
  overflow: hidden;
}
.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  height: 10px;
  margin-bottom: 2px;
}
.table-scrollbar-content {
  height: 1px;
}
.table-wrapper {
  overflow-x: auto;
  overflow-y: visible;
}

/* ---- Modern Table Styles (Reference: studentLeaveRequest) ---- */
.leave-requests-table {
  margin-top: 1rem;
  border-radius: 12px;
  overflow: hidden;
  min-width: 100%;
  width: 100%;
  table-layout: auto;
  border: 1px solid #e5e7eb;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    white-space: nowrap;
  }

  ::v-deep td.journal-cell-wrap {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    vertical-align: top;
  }

  ::v-deep th.journal-cell-account,
  ::v-deep td.journal-cell-account {
    min-width: 140px;
    max-width: 220px;
  }

  ::v-deep th.journal-cell-cheque,
  ::v-deep td.journal-cell-cheque {
    min-width: 120px;
    max-width: 220px;
  }

  ::v-deep th.journal-cell-note,
  ::v-deep td.journal-cell-note {
    min-width: 120px;
    max-width: 220px;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 3px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;
      td { background: #f8fafc; }
    }

    &:nth-child(odd) {
      background: #ffffff;
      td { background: #ffffff; }
    }

    &:hover {
      background: #f3f0ff !important;
      border-left-color: #7c5dfa;
      td {
        background: #f3f0ff !important;
        color: #2d1b69;
        font-weight: 500;
      }
    }
  }

  ::v-deep .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-weight: 600;
    line-height: 1.2;
  }
}

/* ---- Premium Skeleton Loader Styles ---- */
.skeleton-table-wrapper {
  display: block;
  width: 100%;
}
.skeleton-row-item {
  display: flex;
  width: 100%;
  border-bottom: 1px solid #f1f5f9;
  animation: skeletonPulse 2s ease-in-out infinite;
  &:nth-child(1) { animation-delay: 0s; }
  &:nth-child(2) { animation-delay: 0.1s; }
  &:nth-child(3) { animation-delay: 0.2s; }
}
.skeleton-cell-item {
  flex: 1;
  padding: 1.1rem 1rem;
  position: relative;
  &::before {
    content: ""; position: absolute; top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(124, 93, 250, 0.03) 50%, transparent 100%);
    animation: skeletonBorderGlow 2.5s ease-in-out infinite;
  }
}
.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f0f0 0%, #f8f8f8 25%, #f0f0f0 50%, #f8f8f8 75%, #f0f0f0 100%);
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  &::after {
    content: ""; position: absolute; top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(124, 93, 250, 0.08) 30%, rgba(157, 122, 255, 0.12) 50%, rgba(124, 93, 250, 0.08) 70%, transparent 100%);
    animation: skeletonColorWave 2.5s ease-in-out infinite;
    border-radius: 10px;
  }
}

@keyframes skeletonShimmer { 0% { background-position: -300% 0; } 100% { background-position: 300% 0; } }
@keyframes skeletonColorWave { 0%, 100% { opacity: 0.3; transform: scaleX(0.8); } 50% { opacity: 0.6; transform: scaleX(1); } }
@keyframes skeletonBorderGlow { 0%, 100% { opacity: 0; } 50% { opacity: 1; } }
@keyframes skeletonPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.85; transform: scale(0.998); } }

/* Cell Width Controllers */
.skeleton-cell-item:nth-child(1) { flex: 0 0 60px; }
.skeleton-cell-item:nth-child(2) .skeleton-loader { width: 80px; }
.skeleton-cell-item:nth-child(3) .skeleton-loader { width: 100px; }
.skeleton-cell-item:nth-child(4) .skeleton-loader { width: 150px; }
.skeleton-cell-item:nth-child(8) .skeleton-loader { width: 180px; }


/* ---- Cell Styles ---- */
.journal-voucher-cell {
  min-width: 70px;
}
.journal-type-badge, .journal-num-badge {
  font-size: 0.75rem;
  font-weight: 700;
}
.lr-date-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  white-space: nowrap;
}
.lr-date-icon {
  color: #7367f0;
  flex-shrink: 0;
}
.journal-account-title {
  font-size: 0.85rem;
  color: #5e5873;
  display: block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
}
.journal-amount-debit {
  color: #28c76f;
  font-weight: 600;
  font-size: 0.85rem;
}
.journal-amount-credit {
  color: #ea5455;
  font-weight: 600;
  font-size: 0.85rem;
}
.journal-amount-zero {
  color: #b9b9c3;
  font-size: 0.85rem;
}
.journal-total-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}
.journal-total-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6e6b7b;
}
.journal-total-value {
  font-size: 0.85rem;
  color: #5e5873;
}
.journal-cheque-wrap {
  max-width: 100%;
}

.journal-cheque-badge {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  max-width: 100%;
  background: #f3f0ff;
  color: #7367f0;
  border-radius: 10px;
  padding: 4px 8px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
}

.journal-cheque-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.journal-cheque-text {
  min-width: 0;
  flex: 1;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.journal-note-text {
  font-size: 0.82rem;
  color: #6e6b7b;
  display: block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
  max-width: 100%;
}
.text-muted-empty {
  color: #b9b9c3;
  font-size: 0.85rem;
}
.border-top-secondary {
  border-top: 1px solid #ebe9f1;
}
</style>